import * as ramda from 'ramda';

import {Routing} from 'nutshell-core/routing';
import type {CoreEntityType, SavedListType, SavedFilter, Stageset} from 'nutshell-core/types';

import type {EntityType} from 'nutshell-graphql-types';
import type {
    GetSavedFilters_savedFilters_edges_node as SavedList,
    GetDefaultFilters_defaultFilters as DefaultList,
    GetAudiencesForEntityList_audiences_edges_node as Audience,
    GetSavedFilterCount_savedFilterCount as FormattedValue,
    GetSavedFilters_savedFilters_edges_node as SavedFilterGraphQL,
    UpdateSavedFilter_filterUpdate_filter as Filter,
} from 'nutshell-graphql-types';

import {getLeadsDefaultPipelineFilter} from '../../../entities/list/query/sanitize-filters-for-view';
import {isSavedListDirty, isAllEntitiesListDirty} from '../../../saved-filters/saved-filters-utils';
import {getDefaultStagesetId} from '../../../util/get-default-stageset-id';

import type {ShareType} from '../../filtering/share-selection';

import type {NavItemObj, ReduxSavedFilters} from '../entity-list-sidebar/types';

import {DEFAULT_LIST_ID_MAP} from './consts';

export function getSavedFiltersReportType(entityType: CoreEntityType) {
    switch (entityType) {
        case 'accounts':
            return 'ENTITY_LIST_ACCOUNTS';
        case 'contacts':
            return 'ENTITY_LIST_CONTACTS';
        case 'leads':
        default:
            return 'ENTITY_LIST_LEADS';
    }
}

export function getReduxList(
    entityType: CoreEntityType,
    lists: ReduxSavedFilters
): SavedFilter<SavedListType> | null | undefined {
    switch (entityType) {
        case 'contacts':
            return lists[DEFAULT_LIST_ID_MAP.contacts];
        case 'accounts':
            return lists[DEFAULT_LIST_ID_MAP.accounts];
        case 'leads':
        default:
            return lists[DEFAULT_LIST_ID_MAP.leads];
    }
}

export function getNavItemsForDraftSection(
    entityType: CoreEntityType,
    searchParams: any,
    totalCount: FormattedValue | null | undefined,
    selectedCount: FormattedValue | null | undefined,
    savedFilters: ReduxSavedFilters,
    stagesets: Stageset[]
): NavItemObj[] {
    const isActive = isNavItemActive(null, searchParams, entityType, savedFilters);
    const navItems: NavItemObj[] = [
        {
            name: getDefaultListName(entityType, searchParams.view, stagesets, isActive),
            link: getDefaultListLink(entityType, searchParams.view),
            type: 'list',
            count: totalCount,
            countUrl: '',
            isDirty: false,
            isShared: true,
            isImmutable: true,
            isDeleteable: false,
            isActive,
            description: null,
            sharedWith: 'nobody',
        },
    ];

    if (isUserOnUnsavedList(entityType, searchParams, savedFilters)) {
        navItems.push({
            name: 'Unsaved list',
            isUnsavedList: true,
            count: selectedCount,
            countUrl: '',
            type: 'list',
            isDirty: true,
            isActive: true,
            isShared: false,
            isImmutable: false,
            isDeleteable: false,
            description: null,
            sharedWith: 'nobody',
        });
    }

    return navItems;
}

function getDefaultListName(
    entityType: CoreEntityType,
    view: string,
    stagesets: Stageset[],
    isActive: boolean
) {
    switch (entityType) {
        case 'accounts':
            return 'All companies';
        case 'contacts':
            return 'All people';
        case 'leads':
        default:
            return view === 'board'
                ? getLeadsBoardViewDisplayName(window.location.search, stagesets, isActive)
                : 'All leads';
    }
}

export function getLeadsBoardViewDisplayName(
    search: string,
    stagesets: Stageset[],
    isActive: boolean
): string {
    const params = Routing.deparam(search.replace(/^\?/, ''));
    let stagesetName = null;

    if (params.filter && params.filter.stageset && stagesets && stagesets.length > 0) {
        const stagesetId = !isActive ? getDefaultStagesetId() : params.filter.stageset.data[0].data;

        const activeStageset = stagesets.find((stageset) => stageset.id === stagesetId);

        stagesetName = activeStageset ? activeStageset.name : null;
    }

    return stagesetName ? `All leads in ${stagesetName}` : 'All leads in current pipeline';
}

export function getDefaultListLink(entityType: CoreEntityType, view: string) {
    switch (entityType) {
        case 'accounts':
            return `/companies?view=${view}`;
        case 'contacts':
            return `/people?view=${view}`;
        case 'leads':
        default:
            // We want the default list nav item to preserve the current view, but for the board view, it is added by default already
            // and causes an error if we try to add it the same as the others, so we just rely on that default behavior instead
            return view === 'board' ? `/leads` : `/leads?view=${view}`;
    }
}

function isNavItemActive(
    id: string | null | undefined,
    searchParams: any,
    entityType?: CoreEntityType,
    savedFilters?: ReduxSavedFilters
): boolean {
    // Handles instances when displaying 'all entities'
    if (!id && !searchParams.audience && entityType && savedFilters) {
        // If the user is on an accessible saved list, the default item should never be active
        if (
            searchParams.list &&
            userCanAccessSavedList(entityType, savedFilters, searchParams.list)
        ) {
            return false;
        }

        return !isAllEntitiesListDirty(window.location.search, entityType, savedFilters);
    }

    if (searchParams.audience && searchParams.audience === id) {
        return true;
    }

    if (searchParams.list && searchParams.list === id) {
        return true;
    }

    return false;
}

function isUserOnUnsavedList(
    entityType: CoreEntityType,
    searchParams: any,
    savedFilters: ReduxSavedFilters
): boolean {
    if (searchParams.audience) {
        return false;
    }

    if (searchParams.list && userCanAccessSavedList(entityType, savedFilters, searchParams.list)) {
        return false;
    }

    return isAllEntitiesListDirty(window.location.search, entityType, savedFilters);
}

/**
 * If the user ends up on a list that either they don't have access to, or the deprecated default lists
 * then we want to show the 'unsaved list' sidebar option else they will not see any selected list or save button
 *
 * True if the list is not the default and exists in their redux state, else we consider it inaccessible
 *
 * @param entityType The entity type of the saved list
 * @param savedFilters The list of saved filters from our Redux state
 * @param listId The ID of the saved list
 * @returns Whether the user can access the saved list
 */
function userCanAccessSavedList(
    entityType: CoreEntityType,
    savedFilters: ReduxSavedFilters,
    listId: string
): boolean {
    return !isViewingDefaultList(entityType, savedFilters, listId) && Boolean(savedFilters[listId]);
}

function isViewingDefaultList(
    entityType: CoreEntityType,
    savedFilters: ReduxSavedFilters,
    listId: string
): boolean {
    const reduxList = getReduxList(entityType, savedFilters);

    if (reduxList) {
        return reduxList.id === listId;
    }

    return false;
}

export function isBoardViewFilterDifferent(filter: any): boolean {
    const expectedBoardViewFilter = getLeadsDefaultPipelineFilter();

    return !ramda.equals(filter, expectedBoardViewFilter);
}

export function getNavItemsFromSavedFilters(
    savedLists: SavedFilterGraphQL[],
    activeSavedList: SavedFilter<SavedListType> | null | undefined,
    queryString: string,
    searchParams: any,
    selectedCount: FormattedValue | null | undefined,
    isImmutable: boolean
): NavItemObj[] {
    return savedLists.map((list) => {
        const isActive = isNavItemActive(list.id, searchParams);
        const coreEntityType = getCoreEntityType(list.entityType);

        const isDirty = activeSavedList
            ? isListDirty(list.id, queryString, activeSavedList, coreEntityType)
            : false;

        return {
            id: list.id,
            name: list.name,
            type: 'list',
            link: buildLink(list),
            count: isActive && isDirty ? selectedCount : null,
            countUrl: list.countUrl,
            isDirty: isDirty,
            isActive,
            isShared: Boolean(list.isShared || isImmutable),
            isImmutable,
            isDeleteable: Boolean(list.isDeleteable),
            description: list.description,
            sharedWith: getSharedType(list, isImmutable),
        };
    });
}

function getSharedType(list: SavedFilterGraphQL, isImmutable: boolean): ShareType {
    if (!list.isShared && !isImmutable) {
        return 'nobody';
    }

    if (list.sharedWithEntities.length !== 0) {
        return 'individuals';
    }

    return 'everyone';
}

function getCoreEntityType(entityType: EntityType): CoreEntityType {
    switch (entityType) {
        case 'ACCOUNT':
            return 'accounts';
        case 'CONTACT':
            return 'contacts';
        case 'LEAD':
        default:
            return 'leads';
    }
}

function buildLink(list: SavedList | DefaultList | Audience) {
    const link = list.__typename === 'Audience' ? list.htmlUrlPathForList : list.htmlUrlPath;
    const view = Routing.deparam(window.location.search.replace(/^\?/, '')).view;
    const currentView = view || 'list';
    const [basePath, paramsString = ''] = link.split('?');
    const params = Routing.deparam(paramsString);
    params.view = currentView;

    return `${basePath}?${Routing.param(params)}`;
}

export function isListDirty(
    id: string,
    queryString: string,
    activeSavedListFilter: SavedFilter<SavedListType>,
    entityType: CoreEntityType
): boolean {
    return (
        activeSavedListFilter.id === id &&
        isSavedListDirty(queryString, activeSavedListFilter, entityType)
    );
}

export function getNavItemsFromAudiences(
    audiences: Audience[],
    searchParams: any,
    selectedCount?: FormattedValue | null
): NavItemObj[] {
    return audiences.map((audience) => {
        const isActive = isNavItemActive(audience.id, searchParams);

        // This lets us know to use selectedCount from our REST API rather than the count stored in GraphQL
        const filtersApplied = Boolean(
            (searchParams.filter && Object.keys(searchParams.filter).length > 1) || searchParams.q
        );

        return {
            id: audience.id,
            name: audience.name,
            link: buildLink(audience),
            type: 'audience',
            countUrl: '',
            isDirty: false,
            isActive,
            count:
                isActive && filtersApplied && selectedCount
                    ? selectedCount
                    : audience.membershipStats.totalCount,
            isShared: true,
            isImmutable: false,
            isDeleteable: true,
            isProcessing: audience.isRecentlyCreated,
            description: audience.description,
            sharedWith: 'everyone',
        };
    });
}

export function buildSavedListObjectForRedux(
    filter: Filter,
    entityType: CoreEntityType,
    value: string
) {
    return {
        id: filter.id,
        type: 'filters',
        isUpdateable: filter.isUpdateable,
        isDeleteable: filter.isUpdateable,
        createdTime: filter.createdTime,
        href: `${window.location.origin}/rest/filters/${filter.id}`,
        isShared: filter.isShared,
        isImmutable: filter.isImmutable,
        name: filter.name,
        reportType: `entity_list_${entityType}`,
        value: value,
        description: filter.description,
        creatorUserName: filter.creatorUser && filter.creatorUser.name,
        links: {
            user: filter.creatorUser && filter.creatorUser.id,
            usersAndTeams: filter.sharedWithEntities.map<string | null | undefined>(
                (entity) => entity && entity.id
            ),
        },
    };
}

export function getVisibleAndPopoverNavItems(
    navItems: NavItemObj[],
    activeNavItemId: string | null | undefined,
    maxVisibleCount: number
): {
    visibleNavItems: NavItemObj[];
    popoverNavItems: NavItemObj[];
} {
    // @ts-expect-error
    let visibleNavItems = [];
    // @ts-expect-error
    const popoverNavItems = [];

    // If there are exactly maxVisibleCount + 1, no reason to show the
    // text "View 1 more" which takes up the same space as a additional item
    if (navItems.length > maxVisibleCount + 1) {
        navItems.forEach((navItem, index) => {
            const isActive = navItem.id === activeNavItemId;
            if (isActive || index < maxVisibleCount) {
                visibleNavItems.push(navItem);
            } else {
                popoverNavItems.push(navItem);
            }
        });
    } else {
        visibleNavItems = navItems;
    }

    // @ts-expect-error
    return {visibleNavItems, popoverNavItems};
}
